// A small inline-SVG trend for the usage tab (spec 15 §4 S7): one line over
// the days that have a reading, a gap where a day has none (a missing reading
// is never drawn as 0, spec 15 §9.14), a dot for a reading with no neighbour
// to join (a lone "M x y" draws nothing), and the last reading marked. No
// chart library: the shape is simple and the design tokens colour it.

import type { ReactNode } from "react";

export interface SparkPoint {
  date: string;
  value: number | null;
}

const W = 240;
const H = 44;
const PAD = 3;

/** The SVG path for the points, broken at every missing day. */
export function sparkPath(points: readonly SparkPoint[]): string {
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  if (values.length === 0) return "";
  const max = Math.max(...values);
  const min = Math.min(0, ...values);
  const span = max - min || 1;
  const step = points.length > 1 ? (W - PAD * 2) / (points.length - 1) : 0;
  let d = "";
  let pen = false;
  points.forEach((p, i) => {
    if (p.value === null) {
      pen = false;
      return;
    }
    const x = PAD + i * step;
    const y = H - PAD - ((p.value - min) / span) * (H - PAD * 2);
    d += `${pen ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)} `;
    pen = true;
  });
  return d.trim();
}

/** The indices of readings with no reading on either side: a path segment
 * of one point has no length, so each is drawn as a dot instead. */
export function isolatedPoints(points: readonly SparkPoint[]): number[] {
  const out: number[] = [];
  points.forEach((p, i) => {
    if (p.value === null) return;
    const before = i > 0 ? (points[i - 1]?.value ?? null) : null;
    const after = i < points.length - 1 ? (points[i + 1]?.value ?? null) : null;
    if (before === null && after === null) out.push(i);
  });
  return out;
}

export function Sparkline({
  points,
  label,
  "data-testid": testId,
}: {
  points: readonly SparkPoint[];
  /** The accessible description, written by the caller in the reader's language. */
  label: string;
  "data-testid"?: string;
}): ReactNode {
  const d = sparkPath(points);
  const lastIndex = [...points].reverse().findIndex((p) => p.value !== null);
  const last = lastIndex === -1 ? null : points.length - 1 - lastIndex;
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  const max = values.length > 0 ? Math.max(...values) : 0;
  const min = Math.min(0, ...values);
  const span = max - min || 1;
  const step = points.length > 1 ? (W - PAD * 2) / (points.length - 1) : 0;
  const lastValue = last === null ? null : (points.at(last)?.value ?? null);
  const y = (value: number): number => H - PAD - ((value - min) / span) * (H - PAD * 2);
  const lone = isolatedPoints(points).filter((i) => i !== last);
  return (
    <svg
      className="admin-spark"
      viewBox={`0 0 ${String(W)} ${String(H)}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
      data-testid={testId}
    >
      <line className="admin-spark-base" x1={PAD} x2={W - PAD} y1={H - PAD} y2={H - PAD} />
      {d !== "" ? <path className="admin-spark-line" d={d} /> : null}
      {lone.map((i) => (
        <circle
          key={i}
          className="admin-spark-point"
          cx={PAD + i * step}
          cy={y(points[i]?.value ?? 0)}
          r={1.75}
        />
      ))}
      {lastValue !== null && last !== null ? (
        <circle className="admin-spark-dot" cx={PAD + last * step} cy={y(lastValue)} r={2.5} />
      ) : null}
    </svg>
  );
}
